import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { useLanguage } from '../shared/LanguageContext'
import {
  createMutationId,
  displayDate,
  displayHours,
  displayMoney,
  sessionsForClass,
  studentsForClass,
} from './taFormat'
import { taCopy } from './taCopy'
import { SectionUnavailable, TaSubnav } from './TaShell'
import { useTaDraft } from './useTaDraft'
import { releaseConflictedMutationKey } from './taMutationKey'

const CATEGORIES = [
  'teaching_suggestion', 'student_issue', 'leave_request',
  'late_payment', 'missed_attendance', 'feedback_request',
]

const EMPTY_REQUEST = {
  category: 'teaching_suggestion', message: '', class_id: '', session_id: '',
  student_id: '', leave_start_date: '', leave_end_date: '', payment_period: '',
  client_mutation_id: '',
}

function Compensation({ compensation, copy, language }) {
  if (compensation?.status === 'unavailable') {
    return <section className="ta-v2-pay"><div className="ta-v2-section-heading"><h2>{copy('compensation')}</h2></div><p className="ta-v2-unavailable">{copy('payUnavailable')}</p></section>
  }
  if (compensation?.status === 'setup_incomplete') {
    return <section className="ta-v2-pay"><div className="ta-v2-section-heading"><h2>{copy('compensation')}</h2></div><p className="ta-v2-unavailable">{copy('paySetupIncomplete')}</p></section>
  }
  if (compensation?.status !== 'ready' || !compensation.summary) {
    // Range-review M12: the money section must never silently render
    // NOTHING — an absent payload or a future fourth status shows the
    // explicit unavailable copy, like every sibling section.
    return <section className="ta-v2-pay"><div className="ta-v2-section-heading"><h2>{copy('compensation')}</h2></div><p className="ta-v2-unavailable">{copy('payUnavailable')}</p></section>
  }
  const summary = compensation.summary
  const balanceLabel = Number(summary.balance_cents) < 0 ? copy('overpaid') : copy('owed')
  return (
    <section className="ta-v2-pay">
      <div className="ta-v2-pay-hero">
        <p>{balanceLabel}</p>
        <strong>{displayMoney(Math.abs(Number(summary.balance_cents)), language)}</strong>
      </div>
      <dl className="ta-v2-tally">
        <div><dt>{copy('hoursWorked')}</dt><dd>{displayHours(summary.worked_minutes)}</dd></div>
        <div><dt>{copy('earned')}</dt><dd>{displayMoney(summary.earned_cents, language)}</dd></div>
        <div><dt>{copy('paid')}</dt><dd>{displayMoney(summary.paid_cents, language)}</dd></div>
      </dl>
      {compensation.earnings?.length > 0 && (
        <details className="ta-v2-ledger-details">
          <summary>{copy('earningsHistory')}<ChevronDown aria-hidden="true" size={18} /></summary>
          <div className="ta-v2-ruled-list">{compensation.earnings.map((row) => (
            <div className="ta-v2-ledger-row" key={row.id}>
              <span>
                <strong>{displayDate(row.session_date, language)}</strong>
                <small>{displayHours(row.duration_minutes)}</small>
                {row.semester_ended ? <em className="ta-v2-ended-semester">{copy('endedSemester')}</em> : null}
              </span>
              <b>{row.state === 'void' ? '—' : displayMoney(row.amount_cents, language)}</b>
            </div>
          ))}</div>
        </details>
      )}
      <details className="ta-v2-ledger-details">
        <summary>{copy('paymentsHistory')}<ChevronDown aria-hidden="true" size={18} /></summary>
        {compensation.payments?.length ? <div className="ta-v2-ruled-list">{compensation.payments.map((row) => (
          <div className="ta-v2-ledger-row" key={row.id}>
            <span><strong>{displayDate(row.paid_date, language)}</strong><small>{row.method || ''}</small></span>
            <b>{displayMoney(row.amount_cents, language)}</b>
          </div>
        ))}</div> : <p className="ta-v2-empty">{copy('noPayments')}</p>}
      </details>
    </section>
  )
}

function MattFeedback({ section, copy, language }) {
  return (
    <section className="ta-v2-section">
      <div className="ta-v2-section-heading"><h2>{copy('mattFeedback')}</h2></div>
      {section?.status !== 'ready' ? <SectionUnavailable /> : section.data.length ? (
        <div className="ta-v2-feedback-list">{section.data.map((row) => (
          <article key={row.id}>
            <header><strong>{row.class_name || copy('mattFeedback')}</strong><time>{displayDate(row.session_date || row.created_at, language)}</time></header>
            {row.feedback_text && <p>{row.feedback_text}</p>}
            {row.tasks_text && <small>{row.tasks_text}</small>}
          </article>
        ))}</div>
      ) : <p className="ta-v2-empty">{copy('noFeedback')}</p>}
    </section>
  )
}

function ContextFields({ draft, setDraft, schedule, copy, language, requireClass, requireSession }) {
  const sessions = sessionsForClass(schedule, draft.class_id)
  return (
    <div className="ta-v2-form-grid">
      <label>{copy('class')}
        <select value={draft.class_id} required={requireClass} onChange={(event) => setDraft({ ...draft, class_id: event.target.value, session_id: '', student_id: '' })}>
          <option value="">{copy(requireClass ? 'chooseClass' : 'personalOrNA')}</option>
          {(schedule?.classes || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </select>
      </label>
      <label>{requireSession ? copy('session') : copy('optionalSession')}
        <select value={draft.session_id} required={requireSession} onChange={(event) => setDraft({ ...draft, session_id: event.target.value })}>
          <option value="">{copy('chooseSession')}</option>
          {sessions.map((row) => <option key={row.id} value={row.id}>{displayDate(row.session_date, language)} · {copy('session')} {row.session_number}</option>)}
        </select>
      </label>
    </div>
  )
}

function RequestForm({ schedule, scheduleAvailable, copy, language, onAction }) {
  const [draft, setDraft, clearDraft] = useTaDraft('request', EMPTY_REQUEST)
  const [status, setStatus] = useState(null)
  const [saving, setSaving] = useState(false)
  const category = draft.category
  const contextual = ['teaching_suggestion', 'feedback_request', 'student_issue', 'leave_request', 'missed_attendance'].includes(category)
  const students = studentsForClass(schedule, draft.class_id)
  function setCategory(next) {
    setDraft({ ...EMPTY_REQUEST, category: next, message: draft.message, client_mutation_id: draft.client_mutation_id })
    setStatus(null)
  }
  async function submit(event) {
    event.preventDefault()
    setSaving(true)
    setStatus(null)
    const payload = { ...draft, action: 'create_request', client_mutation_id: draft.client_mutation_id || createMutationId() }
    setDraft(payload)
    const result = await onAction(payload)
    setSaving(false)
    if (releaseConflictedMutationKey(result, setDraft)) {
      return setStatus({ type: 'error', message: copy('mutationKeyConflict') })
    }
    if (result.error) return setStatus({ type: 'error', message: result.error })
    clearDraft(EMPTY_REQUEST)
    setStatus({ type: 'success', message: copy('saved') })
  }
  return (
    <section className="ta-v2-work-form" aria-labelledby="ta-request-form-heading">
      <h2 id="ta-request-form-heading">{copy('newRequest')}</h2>
      <form onSubmit={submit}>
        <label>{copy('category')}
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            {CATEGORIES.map((value) => <option key={value} value={value}>{copy(value)}</option>)}
          </select>
        </label>

        {contextual && (
          <>
            {!scheduleAvailable && <SectionUnavailable />}
            <ContextFields
              draft={draft}
              setDraft={setDraft}
              schedule={schedule}
              copy={copy}
              language={language}
              requireClass={category === 'student_issue' || category === 'missed_attendance'}
              requireSession={category === 'missed_attendance'}
            />
          </>
        )}
        {category === 'student_issue' && (
          <label>{copy('student')}
            <select value={draft.student_id} onChange={(event) => setDraft({ ...draft, student_id: event.target.value })} required>
              <option value="">{copy('chooseStudent')}</option>
              {students.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
            </select>
          </label>
        )}
        {category === 'leave_request' && <div className="ta-v2-form-grid">
          <label>{copy('leaveStart')}<input type="date" value={draft.leave_start_date} onChange={(event) => setDraft({ ...draft, leave_start_date: event.target.value })} required /></label>
          <label>{copy('leaveEnd')}<input type="date" min={draft.leave_start_date} value={draft.leave_end_date} onChange={(event) => setDraft({ ...draft, leave_end_date: event.target.value })} required /></label>
        </div>}
        {category === 'late_payment' && <label>{copy('paymentPeriod')}<input value={draft.payment_period} onChange={(event) => setDraft({ ...draft, payment_period: event.target.value })} required /></label>}
        <label>{copy('message')}<textarea rows="4" value={draft.message} onChange={(event) => setDraft({ ...draft, message: event.target.value })} required /></label>
        <button className="ta-v2-primary-button" disabled={saving}>{saving ? copy('saving') : copy('sendRequest')}</button>
        {status && <p className={`ta-v2-form-status is-${status.type}`} role="status">{status.message}</p>}
      </form>
    </section>
  )
}

function RequestHistory({ section, copy, language }) {
  return (
    <section className="ta-v2-section">
      <div className="ta-v2-section-heading"><h2>{copy('requestHistory')}</h2><span>{section?.data?.length || 0}</span></div>
      {section?.status !== 'ready' ? <SectionUnavailable /> : section.data.length ? (
        <div className="ta-v2-request-list">{section.data.map((row) => (
          <article key={row.id}>
            <header><strong>{copy(row.category)}</strong><span>{copy(row.status)}</span></header>
            <p>{row.message}</p>
            {row.matt_response && <blockquote>{row.matt_response}</blockquote>}
            <small>{displayDate(row.created_at, language)}</small>
          </article>
        ))}</div>
      ) : <p className="ta-v2-empty">{copy('noRequests')}</p>}
    </section>
  )
}

export default function TaMeView({ home, onAction, focusTarget }) {
  const { language } = useLanguage()
  const copy = taCopy(language)
  const [activeTab, setActiveTab] = useState(() => focusTarget === 'request' ? 'mattComm' : 'finance')
  const scheduleAvailable = home.schedule?.status === 'ready'
  const schedule = scheduleAvailable ? home.schedule.data : { classes: [], students: [], upcoming_sessions: [], past_sessions: [] }
  return (
    <div className="ta-v2-view">
      <TaSubnav
        tabs={['finance', 'mattComm'].map((tab) => ({ id: tab, label: copy(tab) }))}
        active={activeTab}
        onChange={setActiveTab}
        label={copy('me')}
        idPrefix="ta-me"
        twoColumns
      />

      <div id={`ta-me-panel-${activeTab}`} role="tabpanel" aria-labelledby={`ta-me-tab-${activeTab}`}>
        {activeTab === 'finance' && <Compensation compensation={home.compensation} copy={copy} language={language} />}
        {activeTab === 'mattComm' && <>
          <RequestForm schedule={schedule} scheduleAvailable={scheduleAvailable} copy={copy} language={language} onAction={onAction} />
          <MattFeedback section={home.feedback} copy={copy} language={language} />
          <RequestHistory section={home.requests} copy={copy} language={language} />
        </>}
      </div>
    </div>
  )
}
